import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import "@/App.css";
import { SKILandingPage } from "@/pages/SKILandingPage";
import { PricingPage } from "@/pages/PricingPage";
import { LandingPage, ApplicationForm } from "@/pages/LandingAndApplication";
import CompanyDashboard from "@/pages/CompanyDashboard";
import ChatWithNova from "@/pages/ChatWithNova";
import Dashboard from "@/pages/Dashboard";
import GovernanceDashboard from "@/pages/GovernanceDashboard";
import MentorsPage from "@/pages/MentorsPage";
import FoundersDirectory from "@/pages/FoundersDirectory";
import EmailCampaignPage from "@/pages/EmailCampaignPage";
import AdminDashboard from "@/pages/AdminDashboard";
import InvestorPage from "@/pages/InvestorPage";
import AuthPage from "@/pages/AuthPage";
import ApplicationSubmitPage from "@/pages/ApplicationSubmitPage";
import MentorRegistrationPage from "@/pages/MentorRegistrationPage";
import MentorWorkshopPreview from "@/pages/MentorWorkshopPreview";
import MentorProfileEdit from "@/pages/MentorProfileEdit";
import PitchDayPage from "@/pages/PitchDayPage";
import MLOpsDashboard from "@/pages/MLOpsDashboard";
import FounderDashboard from "@/pages/FounderDashboard";
import MyCohort from "@/pages/MyCohort";
import CohortApply from "@/pages/CohortApply";
import AdminCohortApplicants from "@/pages/AdminCohortApplicants";
import InvestorDashboard from "@/pages/InvestorDashboard";
import MyApplicationsPage from "@/pages/MyApplicationsPage";
import PaymentSuccessPage from "@/pages/PaymentSuccessPage";
import AboutPage from "@/pages/AboutPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import InvestorPipelinePage from "@/pages/InvestorPipelinePage";
import FinancialDashboard from "@/pages/FinancialDashboard";
import JobsPage from "@/pages/JobsPage";
import AuthCallback from "@/pages/AuthCallback";
// New investor-ready pages
import StartupDashboard from "@/pages/StartupDashboard";
import InvestorPortal from "@/pages/InvestorPortal";
import ValuationCalculator from "@/pages/ValuationCalculator";
import KYCPage from "@/pages/KYCPage";
import SecuritySettings from "@/pages/SecuritySettings";
import MediaPage from "@/pages/MediaPage";
import CompanyJobDashboard from "@/pages/CompanyJobDashboard";
import WorkflowAutomation from "@/pages/WorkflowAutomation";
import MasterclassPage from "@/pages/MasterclassPage";
import InvestUAEForum2026 from "@/pages/InvestUAEForum2026";
import ForumThankYou from "@/pages/ForumThankYou";
import CohortApplication from "@/pages/CohortApplication";
import AcceleratorPage from "@/pages/AcceleratorPage";
import ReferPage from "@/pages/ReferPage";
import ChaptersPage from "@/pages/ChaptersPage";
import CommunityPage from "@/pages/CommunityPage";
import WebinarsPage from "@/pages/WebinarsPage";
import WebinarThankYou from "@/pages/WebinarThankYou";
import AdminWebinarsPage from "@/pages/AdminWebinarsPage";
import AdminLeadsPage from "@/pages/AdminLeadsPage";
import AdminForumDashboardPage from "@/pages/AdminForumDashboardPage";
import ChaptersApply from "@/pages/ChaptersApply";
import CompanyCareersPage from "@/pages/CompanyCareersPage";
import SpeakerProfile from "@/pages/SpeakerProfile";
import AdminEventsPage from "@/pages/AdminEventsPage";
import FounderDNAQuiz from "@/pages/FounderDNAQuiz";
import FacultyBooking from "@/pages/FacultyBooking";
import BlogIndex from "@/pages/BlogIndex";
import BlogArticle from "@/pages/BlogArticle";
import AxisIndexPage from "@/pages/AxisIndexPage";
import AxisIndexCohort1Page from "@/pages/AxisIndexCohort1Page";
import AxisIndexCohort1IntakePage from "@/pages/AxisIndexCohort1IntakePage";
import AxisIndexCohort1ScorecardPage from "@/pages/AxisIndexCohort1ScorecardPage";
import AxisCircuitPage from "@/pages/AxisCircuitPage";
import ProgrammesPage from "@/pages/ProgrammesPage";
import FloatingEventWidget from "@/components/FloatingEventWidget";
import ErrorBoundary from "@/components/ErrorBoundary";
import { AdminRoute } from "@/components/ProtectedRoute";
import { ChatWidget } from "@/components/ChatWidget";
import { Toaster } from "@/components/ui/toaster";

// Conditional ChatWidget - hide on masterclass page
const ConditionalChatWidget = () => {
  const location = useLocation();
  // Hide global chat on masterclass page (has its own) and on the event landing
  if (location.pathname === '/masterclass') return null;
  if (location.pathname.startsWith('/events/')) return null;
  return <ChatWidget />;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<SKILandingPage />} />
            <Route path="/accelerator" element={<AcceleratorPage />} />
            <Route path="/programme" element={<AcceleratorPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/login" element={<AuthPage />} />
            <Route path="/signup" element={<AuthPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/apply" element={<CohortApplication />} />
            <Route path="/refer" element={<ReferPage />} />
            <Route path="/referrals" element={<ReferPage />} />
            <Route path="/chapters" element={<ChaptersPage />} />
            <Route path="/chapters/apply" element={<ChaptersApply />} />
            <Route path="/chapter-leads" element={<ChaptersPage />} />
            <Route path="/community" element={<CommunityPage />} />
            <Route path="/whatsapp" element={<CommunityPage />} />
            <Route path="/join" element={<CommunityPage />} />
            <Route path="/webinars" element={<WebinarsPage />} />
            <Route path="/webinar" element={<WebinarsPage />} />
            <Route path="/webinars/:slug/thank-you" element={<WebinarThankYou />} />
            <Route path="/admin/webinars" element={<AdminWebinarsPage />} />
            <Route path="/admin/leads" element={<AdminLeadsPage />} />
            <Route path="/admin/forum" element={<AdminForumDashboardPage />} />
            <Route path="/admin/forum/dashboard" element={<AdminForumDashboardPage />} />
            <Route path="/admin" element={<AdminLeadsPage />} />
            <Route path="/jobs/company/:slug" element={<CompanyCareersPage />} />
            <Route path="/careers/:slug" element={<CompanyCareersPage />} />
            <Route path="/apply-form" element={<ApplicationSubmitPage />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/governance" element={<GovernanceDashboard />} />
            <Route path="/mentors" element={<MentorsPage />} />
            <Route path="/founders" element={<FoundersDirectory />} />
            <Route path="/email-campaign" element={<AdminRoute><EmailCampaignPage /></AdminRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/investor" element={<InvestorPage />} />
            <Route path="/mentor-registration" element={<MentorRegistrationPage />} />
            <Route path="/mentor-workshop-preview" element={<MentorWorkshopPreview />} />
          <Route path="/mentor/profile" element={<MentorProfileEdit />} />
            <Route path="/pitch-day" element={<PitchDayPage />} />
            <Route path="/mlops" element={<MLOpsDashboard />} />
            <Route path="/founder-dashboard" element={<FounderDashboard />} />
            <Route path="/my-cohort" element={<MyCohort />} />
            <Route path="/cohort-1/apply" element={<CohortApplication />} />
            <Route path="/cohort/apply" element={<CohortApplication />} />
            <Route path="/apply-cohort" element={<CohortApplication />} />
            <Route path="/admin/cohort-applicants" element={<AdminRoute><AdminCohortApplicants /></AdminRoute>} />
            <Route path="/investor-dashboard" element={<InvestorDashboard />} />
            <Route path="/investor-pipeline" element={<InvestorPipelinePage />} />
            <Route path="/financials" element={<FinancialDashboard />} />
            <Route path="/jobs" element={<JobsPage />} />
            <Route path="/my-applications" element={<MyApplicationsPage />} />
            <Route path="/company-jobs" element={<CompanyJobDashboard />} />
            <Route path="/workflows" element={<WorkflowAutomation />} />
            <Route path="/masterclass" element={<MasterclassPage />} />
            <Route path="/events/invest-uae-forum-2026" element={<InvestUAEForum2026 />} />
            <Route path="/forum/thank-you" element={<ForumThankYou />} />
            <Route path="/events/invest-uae-forum-2026/thank-you" element={<ForumThankYou />} />
            <Route path="/events/invest-uae-forum-2026/speakers/:speakerSlug" element={<SpeakerProfile />} />
            <Route path="/forum/speakers/:speakerSlug" element={<SpeakerProfile />} />
            <Route path="/events/uae-forum" element={<InvestUAEForum2026 />} />
            <Route path="/events" element={<InvestUAEForum2026 />} />
            <Route path="/admin/events" element={<AdminRoute><AdminEventsPage /></AdminRoute>} />
            <Route path="/founder-dna" element={<FounderDNAQuiz />} />
            <Route path="/dna" element={<FounderDNAQuiz />} />
            <Route path="/founder/faculty" element={<FacultyBooking />} />
            <Route path="/faculty-booking" element={<FacultyBooking />} />
            <Route path="/blog" element={<BlogIndex />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/axis-index" element={<AxisIndexPage />} />
            <Route path="/axis-index/cohort1" element={<AxisIndexCohort1Page />} />
            <Route path="/axis-index/cohort1/intake" element={<AxisIndexCohort1IntakePage />} />
            <Route path="/axis-index/cohort1/scorecard/:session_id" element={<AxisIndexCohort1ScorecardPage />} />
            <Route path="/axis-circuit" element={<AxisCircuitPage />} />
            <Route path="/the-axis-circuit" element={<AxisCircuitPage />} />
            <Route path="/programmes" element={<ProgrammesPage />} />
            <Route path="/programs" element={<ProgrammesPage />} />
            <Route path="/the-axis-index" element={<AxisIndexPage />} />
            <Route path="/payment-success" element={<PaymentSuccessPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/media" element={<MediaPage />} />
            {/* New Investor-Ready Routes */}
            <Route path="/startup-dashboard" element={<StartupDashboard />} />
            <Route path="/investor-portal" element={<InvestorPortal />} />
            <Route path="/valuation-calculator" element={<ValuationCalculator />} />
            <Route path="/kyc" element={<KYCPage />} />
            <Route path="/security" element={<SecuritySettings />} />
            <Route path="/company/:companyId" element={<CompanyDashboard />} />
            <Route path="/chat" element={<ChatWithNova />} />
            <Route path="/chat/:companyId" element={<ChatWithNova />} />
          </Routes>
          <ConditionalChatWidget />
          <FloatingEventWidget />
        </BrowserRouter>
        <Toaster />
      </div>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
